import React, { useState, useEffect, useRef } from "react";
import FeatherIcon from "feather-icons-react";
import { getInitials } from "../constants";

export const SmoothAvatar = ({ src, name }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const imgRef = useRef(null);

    // This component sits at a fixed slot/row position, so the SAME instance
    // (and its `loaded`/`error` state) survives across prop updates - a slot
    // that had no photo, then an error-ed load, keeps rendering whatever that
    // state happened to be even once `src` later points at a perfectly good
    // photo. Resetting on every `src` change is what lets a later successful
    // load actually show up instead of being stuck behind one earlier failure.
    //
    // A browser-cached image finishes loading SYNCHRONOUSLY as soon as its
    // src is set, often before this effect has even attached the <img>'s own
    // onLoad listener - the `load` event never fires again for an already-
    // complete image, so `loaded` got stuck false forever and the no-photo
    // icon stayed stacked on top of a perfectly good, fully-loaded photo.
    // Looked exactly like "sometimes shows the photo, sometimes doesn't" when
    // switching tabs, because it was random whether this effect lost that
    // race against the browser's own cache hit. Checking `.complete` here
    // catches the case the event itself misses.
    useEffect(() => {
        if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
            setLoaded(true);
            setError(false);
        } else {
            setLoaded(false);
            setError(false);
        }
    }, [src]);

    if (!src || error) {
        return name
            ? <div className="no-thumbnail initials">{getInitials(name)}</div>
            : <div className="no-thumbnail"><FeatherIcon icon="user" /></div>;
    }

    return (
        <div style={{ position: "relative", display: "inline-flex", justifyContent: "center", alignItems: "center" }}>
            <img
                ref={imgRef}
                src={src}
                className="thumbnail"
                onLoad={() => setLoaded(true)}
                onError={() => setError(true)}
                style={{
                    opacity: loaded ? 1 : 0,
                    transition: "opacity 0.4s ease"
                }}
            />
            <div
                className="no-thumbnail"
                style={{
                    position: "absolute",
                    opacity: loaded ? 0 : 1,
                    transition: "opacity 0.4s ease",
                    pointerEvents: "none"
                }}
            >
                <FeatherIcon icon="user" />
            </div>
        </div>
    );
};
